import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef } from 'react';
import { Button, Input } from '../Simple';
import useSessionState from '../../Hooks/useSessionState';
import usePricerTypeLookupApi from "../../Hooks/usePricerTypeLookupApi";
import ApiError from "./ApiError";
import { ESRD, HH, IPPS, LTCH, OPPS, PFS, PSYCH, REHAB, SNF, FQHC } from "../../Constants/modelTypes";

export default function PricerSelection() {
    const navigate = useNavigate();
    const pricerSelectionFirstInputRef = useRef(null);

    const [pos, setPos] = useSessionState('', 'placeOfService');
    const [billType, setBillType] = useSessionState('', 'billType');
    const [providerId, setProviderId] = useSessionState('', 'npi');
    const [showInstructions, setShowInstructions] = useSessionState(false, 'showAutoPricerInstructions');

    const pricerType = usePricerTypeLookupApi();

    const handleSubmit = async (e) => {
        await pricerType.execute(formatRequest());
    };

    const toggleInstructions = () => {
        setShowInstructions(!showInstructions);
    };

    useEffect(() => {
        handleReset();
        if (pricerSelectionFirstInputRef.current) {
            pricerSelectionFirstInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (pricerType.success) {
            const pricerTypeResponse = pricerType.result;

            switch (pricerTypeResponse?.pricerType.toLowerCase() ?? '') {
                case IPPS:
                    navigate('/pricer/ip/acute', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case SNF:
                    navigate('/pricer/snf', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case HH:
                    navigate('/pricer/hh', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                            prePopulatedBillType: billType,
                        }
                    });
                    break;
                case LTCH:
                    navigate('/pricer/ltch', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case REHAB:
                    navigate('/pricer/ip/rehab', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case PSYCH:
                    navigate('/pricer/ip/psych', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case OPPS:
                    navigate('/pricer/opps', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedPlaceOfService: pricerTypeResponse.defaultPos,
                            prePopulatedNPI: providerId,
                            prePopulatedBillType: billType,
                        }
                    });
                    break;
                case PFS:
                    navigate('/pricer/pfs', {
                        state: {
                            prePopulatedProviderNPI: providerId,
                            prePopulatedPlaceOfService: pricerTypeResponse.defaultPos,
                            prePopulatedSpecialtyCode: pricerTypeResponse.specialtyCode
                        }
                    });
                    break;
                case ESRD:
                    navigate('/pricer/esrd', {
                        state: {
                            prePopulatedMedicareId: pricerTypeResponse.medicareId,
                            prePopulatedNPI: providerId,
                        }
                    });
                    break;
                case FQHC:
                    navigate('/pricer/fqhc', {
                        state: {
                            prePopulatedProviderNPI: providerId,
                            prePopulatedBillType: billType,
                        }
                    });
                    break;
                default:
                    navigate('/');
                    break;
            }
        }
    }, [pricerType.success])

    const resetApis = () => {
        pricerType.reset();
    };

    const handleReset = () => {
        resetApis();
        setProviderId('');
        setPos('');
        setBillType('');
        if (pricerSelectionFirstInputRef.current) {
            pricerSelectionFirstInputRef.current.focus();
        }
    };

    const formatRequest = () => {
        const request = {
            Pos: pos.trim(),
            BillType: billType.trim(),
            ProviderId: providerId.trim(),
        }
        return request;
    }

    return (
        <>
            <p>The Pricer Selector will automatically navigate to a pricer based on the input into the following three fields.</p>
            <div className="row align-items-start">
                <div className="col">
                    <Input
                        ref={pricerSelectionFirstInputRef}
                        label="Billing Provider ID (NPI)" id="provider-id" labelClassName="fw-bold"
                        value={providerId}
                        onChange={setProviderId}
                        warningPattern="^(\d{10})?$"
                        warningMessage="Typically 10 digits"
                    />
                </div>
                <div className="col">
                    <Input
                        label="Bill Type" id="bill-type" labelClassName="fw-bold"
                        value={billType}
                        onChange={setBillType}
                        warningPattern="^(\d{3})?$"
                        warningMessage="Typically 3 digits"
                    />
                </div>
                <div className="col">
                    <Input
                        label="POS" id="pos" labelClassName="fw-bold"
                        value={pos}
                        onChange={setPos}
                        warningPattern="^(\d{2})?$"
                        warningMessage="Typically 2 digits"
                    />
                </div>
            </div>
            <div className="row mt-4">
                <div className="col d-flex justify-content-end align-items-center ">
                    <div className="row g-0">
                        <div className="col-auto">
                            <Button className="btn-outline-secondary" id="reset-button"
                                onClick={handleReset}
                                disabled={pricerType.loading}
                                text="Reset" />
                        </div>
                        <div className="col-auto ms-2">
                            <Button className="btn-primary" text="Select" onClick={handleSubmit} disabled={pricerType.loading} loading={pricerType.loading} />
                        </div>
                    </div>
                </div>
            </div>
            <hr />
            <button className="btn btn-secondary mb-3" onClick={toggleInstructions}>
                {showInstructions ? 'Hide Instructions' : 'Show Instructions'}
            </button>
            {showInstructions && (
                <div>
                    <h6>Instructions:</h6>
                    <ol>
                        <li>
                            Enter the <strong>Billing Provider ID (NPI)</strong>:
                            <ul>
                                <li>If you have a National Provider Identifier (NPI) for the billing provider on your claim, enter it in this field.</li>
                                <li>The NPI is a unique 10-digit identification number issued to each health care providers in the United States by the Centers for Medicare and Medicaid Services (CMS).</li>
                            </ul>
                        </li>
                        <li>
                            Enter the <strong>Bill Type</strong>:
                            <ul>
                                <li>If your claim specifies a type of bill, enter it in this field.</li>
                                <li>Bill types are typically categorized by a three digit numeric code that indicates the facility type, type of care, and sequence of the claim (e.g., inpatient, outpatient, etc.).</li>
                                <li>Example: For an inpatient hospital claim, you might enter "111".</li>
                            </ul>
                        </li>
                        <li>
                            Enter the <strong>POS (Place of Service)</strong>:
                            <ul>
                                <li>If your claim includes a Place of Service code, enter it in this field.</li>
                                <li>The POS is a two digit code that indicates where services were provided (e.g., office, hospital, emergency room, etc.).</li>
                                <li>Example: For services provided in a physician's office, you might enter "11".</li>
                            </ul>
                        </li>
                        <li>Once all fields are filled, click the <strong>Select</strong> button to determine the appropriate pricer.</li>
                        <li>If you need to reset the fields, click the <strong>Reset</strong> button.</li>
                    </ol>
                </div>
            )}
            <ApiError error={pricerType.error} />
        </>
    )
}